import React from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  LayoutDashboard,
  FolderKanban,
  FileText,
  BarChart3,
  Users,
  Settings,
  LogOut,
  Shield,
  ClipboardList,
  Upload,
  Eye,
  CheckCircle
} from 'lucide-react';

const Sidebar = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const adminLinks = [
    { to: '/admin', icon: LayoutDashboard, label: 'Dashboard' },
    { to: '/admin/projects', icon: FolderKanban, label: 'Projects' },
    { to: '/admin/reports', icon: FileText, label: 'Reports' },
    { to: '/admin/analytics', icon: BarChart3, label: 'Analytics' },
    { to: '/admin/users', icon: Users, label: 'Users' },
    { to: '/admin/settings', icon: Settings, label: 'Settings' },
  ];

  const agentLinks = [
    { to: '/agent', icon: LayoutDashboard, label: 'Dashboard' },
    { to: '/agent/projects', icon: FolderKanban, label: 'My Projects' },
    { to: '/agent/submit-report', icon: Upload, label: 'Submit Report' },
    { to: '/agent/reports', icon: FileText, label: 'My Reports' },
    { to: '/agent/settings', icon: Settings, label: 'Settings' },
  ];

  const donorLinks = [
    { to: '/donor', icon: LayoutDashboard, label: 'Dashboard' },
    { to: '/donor/projects', icon: FolderKanban, label: 'Funded Projects' },
    { to: '/donor/reports', icon: FileText, label: 'View Reports' },
    { to: '/donor/analytics', icon: BarChart3, label: 'Analytics' },
    { to: '/donor/settings', icon: Settings, label: 'Settings' },
  ];

  const getLinks = () => {
    switch (user?.role) {
      case 'admin':
        return adminLinks;
      case 'agent':
        return agentLinks;
      case 'donor':
        return donorLinks;
      default:
        return agentLinks;
    }
  };

  const getRoleIcon = () => {
    switch (user?.role) {
      case 'admin':
        return <Shield className="w-4 h-4" />;
      case 'agent':
        return <ClipboardList className="w-4 h-4" />;
      case 'donor':
        return <Eye className="w-4 h-4" />;
      default:
        return <CheckCircle className="w-4 h-4" />;
    }
  };

  const getRoleLabel = () => {
    switch (user?.role) {
      case 'admin':
        return 'Administrator';
      case 'agent':
        return 'Field Agent';
      case 'donor':
        return 'Donor';
      default:
        return 'User';
    }
  };

  const links = getLinks();

  return (
    <aside className="dashboard-sidebar" data-testid="sidebar">
      {/* Logo */}
      <div className="p-6 border-b border-slate-200">
        <NavLink to="/" className="flex items-center gap-2">
          <img 
            src="https://customer-assets.emergentagent.com/job_80a6bcee-7228-4b60-b359-6ff952ed0f3c/artifacts/ufoinc0k_logo1-removebg-preview.png" 
            alt="Verify Logo" 
            className="h-28 w-auto"
          />
        </NavLink>
      </div>

      {/* User info */}
      <div className="p-4 border-b border-slate-200">
        <div className="flex items-center gap-3">
          {user?.picture ? (
            <img
              src={user.picture}
              alt={user.name}
              className="w-10 h-10 rounded-full object-cover"
            />
          ) : (
            <div className="w-10 h-10 bg-slate-200 rounded-full flex items-center justify-center">
              <span className="text-slate-600 font-medium">
                {user?.name?.charAt(0) || 'U'}
              </span>
            </div>
          )}
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-slate-900 truncate">{user?.name}</p>
            <div className="flex items-center gap-1.5 text-xs text-slate-500">
              {getRoleIcon()}
              <span>{getRoleLabel()}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <nav className="p-4 space-y-1" data-testid="sidebar-nav">
        {links.map((link) => (
          <NavLink
            key={link.to}
            to={link.to}
            end={link.to === '/admin' || link.to === '/agent' || link.to === '/donor'}
            className={({ isActive }) =>
              `sidebar-item ${isActive ? 'active' : ''}`
            }
            data-testid={`nav-${link.label.toLowerCase().replace(/\s/g, '-')}`}
          >
            <link.icon className="w-5 h-5" />
            <span>{link.label}</span>
          </NavLink>
        ))}
      </nav>

      {/* Logout */}
      <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-slate-200 bg-slate-50">
        <button
          onClick={handleLogout}
          className="sidebar-item w-full text-red-600 hover:bg-red-50 hover:text-red-700"
          data-testid="logout-btn"
        >
          <LogOut className="w-5 h-5" />
          <span>Logout</span>
        </button>
      </div>
    </aside>
  );
};

export default Sidebar;
